import { useState } from 'react';
import { MovieStats, Movie } from '../types/movie';
import { BarChart3, TrendingUp, Heart, Star, Calendar, ChevronDown, ChevronUp, Trash2, Edit, Check, X } from 'lucide-react';

interface StatisticsProps {
  stats: MovieStats;
  movies: Movie[];
  onClose: () => void;
  onMovieClick?: (movie: Movie) => void;
  onDeleteMovie?: (movieId: number) => void;
  onUpdateScore?: (movieId: number, newScore: number) => void;
  isAuthenticated?: boolean;
}

export const Statistics: React.FC<StatisticsProps> = ({ stats, movies, onClose, onMovieClick, onDeleteMovie, onUpdateScore, isAuthenticated = false }) => {
  const [expandedSections, setExpandedSections] = useState<{
    summary: boolean;
    recentMovies: boolean;
    recentHighRated: boolean;
  }>({
    summary: false,
    recentMovies: true,
    recentHighRated: true,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Get last 10 movies watched (sorted by seen date)
  const last10Movies = movies
    .filter(movie => {
      if (!movie.lastSeen) return false;
      // Check if lastSeen is a valid date string
      const dateStr = movie.lastSeen.toString().trim();
      if (!dateStr || dateStr === '-' || dateStr === 'null' || dateStr === 'undefined') return false;
      // Try to parse the date
      const date = new Date(dateStr);
      return !isNaN(date.getTime());
    })
    .sort((a, b) => {
      const dateA = new Date(a.lastSeen!);
      const dateB = new Date(b.lastSeen!);
      return dateB.getTime() - dateA.getTime();
    })
    .slice(0, 10);

  // Get last 10 movies rated above 7.0
  const last10HighRated = movies
    .filter(movie => {
      if (!movie.lastSeen || movie.score < 7.0) return false;
      // Check if lastSeen is a valid date string
      const dateStr = movie.lastSeen.toString().trim();
      if (!dateStr || dateStr === '-' || dateStr === 'null' || dateStr === 'undefined') return false;
      // Try to parse the date
      const date = new Date(dateStr);
      return !isNaN(date.getTime());
    })
    .sort((a, b) => {
      const dateA = new Date(a.lastSeen!);
      const dateB = new Date(b.lastSeen!);
      return dateB.getTime() - dateA.getTime();
    })
    .slice(0, 10);

  // Calculate additional stats
  const moviesAbove9 = movies.filter(movie => movie.score >= 9.0).length;
  const moviesBelow5 = movies.filter(movie => movie.score < 5.0).length;

  // State for editing scores
  const [editingScore, setEditingScore] = useState<number | null>(null);
  const [tempScore, setTempScore] = useState<number>(0);

  const handleEditScore = (movie: Movie) => {
    setEditingScore(movie.id);
    setTempScore(movie.score);
  };

  const handleSaveScore = (movieId: number) => {
    if (onUpdateScore) {
      onUpdateScore(movieId, tempScore);
    }
    setEditingScore(null);
  };

  const handleCancelEdit = () => {
    setEditingScore(null);
  };

  const handleDeleteMovie = (movieId: number) => {
    if (onDeleteMovie && window.confirm('Are you sure you want to delete this movie?')) {
      onDeleteMovie(movieId);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-3">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center space-x-2">
            <BarChart3 className="h-5 w-5 sm:h-6 sm:w-6" />
            <span>Movie Statistics</span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <span className="sr-only">Close</span>
            <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-6 sm:space-y-8">
          {/* Summary Statistics with Dropdown */}
          <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
            <div className="space-y-3">
              <div 
                className="flex items-center justify-between p-3 bg-white rounded cursor-pointer hover:bg-gray-50"
                onClick={() => toggleSection('summary')}
              >
                <span className="font-medium text-sm sm:text-base">Total # movies watched: {stats.totalMovies}</span>
                {expandedSections.summary ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
              <div 
                className="flex items-center justify-between p-3 bg-white rounded cursor-pointer hover:bg-gray-50"
                onClick={() => toggleSection('summary')}
              >
                <span className="font-medium text-sm sm:text-base"># movies rated above 7.0: {stats.moviesAbove7}</span>
                {expandedSections.summary ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
              <div 
                className="flex items-center justify-between p-3 bg-white rounded cursor-pointer hover:bg-gray-50"
                onClick={() => toggleSection('summary')}
              >
                <span className="font-medium text-sm sm:text-base"># movies rated above 9.0: {moviesAbove9}</span>
                {expandedSections.summary ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
              <div 
                className="flex items-center justify-between p-3 bg-white rounded cursor-pointer hover:bg-gray-50"
                onClick={() => toggleSection('summary')}
              >
                <span className="font-medium text-sm sm:text-base"># movies rated below 5.0: {moviesBelow5}</span>
                {expandedSections.summary ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center mb-2">
                <Calendar className="h-8 w-8 text-blue-600" />
              </div>
              <div className="text-2xl font-bold text-blue-900">{stats.totalMovies}</div>
              <div className="text-sm text-blue-700">Total Movies</div>
            </div>

            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center mb-2">
                <Star className="h-8 w-8 text-green-600" />
              </div>
              <div className="text-2xl font-bold text-green-900">{stats.averageScore}</div>
              <div className="text-sm text-green-700">Average Score</div>
            </div>

            <div className="bg-red-50 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center mb-2">
                <Heart className="h-8 w-8 text-red-600" />
              </div>
              <div className="text-2xl font-bold text-red-900">{stats.moviesWithGF}</div>
              <div className="text-sm text-red-700">With GF</div>
            </div>

            <div className="bg-purple-50 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center mb-2">
                <TrendingUp className="h-8 w-8 text-purple-600" />
              </div>
              <div className="text-2xl font-bold text-purple-900">{stats.moviesAbove7}</div>
              <div className="text-sm text-purple-700">Above 7.0</div>
            </div>
          </div>

          {/* Top Genres */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Genres</h3>
            <div className="space-y-3">
              {stats.topGenres.map((genre, index) => (
                <div key={genre.genre} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-500 w-6">{index + 1}</span>
                    <span className="text-sm font-medium text-gray-900">{genre.genre}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full"
                        style={{ width: `${(genre.count / stats.totalMovies) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-8 text-right">{genre.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Directors */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Directors</h3>
            <div className="space-y-3">
              {stats.topDirectors.map((director, index) => (
                <div key={director.director} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-500 w-6">{index + 1}</span>
                    <span className="text-sm font-medium text-gray-900">{director.director}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full"
                        style={{ width: `${(director.count / stats.totalMovies) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-8 text-right">{director.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Score Distribution</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>9.0+ (Excellent)</span>
                  <span className="font-medium">{stats.moviesAbove7}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>7.0-8.9 (Good)</span>
                  <span className="font-medium">{stats.moviesAbove7}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>5.0-6.9 (Average)</span>
                  <span className="font-medium">{stats.totalMovies - stats.moviesAbove7}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>&lt;5.0 (Poor)</span>
                  <span className="font-medium">0</span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Watching Habits</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Movies with GF</span>
                  <span className="font-medium">{stats.moviesWithGF}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Movies alone</span>
                  <span className="font-medium">{stats.totalMovies - stats.moviesWithGF}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>GF percentage</span>
                  <span className="font-medium">
                    {stats.totalMovies > 0 ? Math.round((stats.moviesWithGF / stats.totalMovies) * 100) : 0}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Last 10 Movies Watched Table */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div 
              className="flex items-center justify-between mb-4 cursor-pointer"
              onClick={() => toggleSection('recentMovies')}
            >
              <h3 className="text-lg font-semibold text-gray-900">Last 10 movies watched</h3>
              {expandedSections.recentMovies ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
            {expandedSections.recentMovies && (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg overflow-hidden">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Year</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Name</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Genres</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Seen</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Score</th>
                      {isAuthenticated && (
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {last10Movies.length === 0 ? (
                      <tr>
                        <td colSpan={isAuthenticated ? 6 : 5} className="px-4 py-4 text-center text-sm text-gray-500">
                          No movies with "Last Seen" dates found
                        </td>
                      </tr>
                    ) : (
                      last10Movies.map((movie, index) => (
                      <tr key={movie.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2 text-sm text-gray-900">{movie.year}</td>
                        <td className="px-4 py-2 text-sm font-medium">
                          {onMovieClick ? (
                            <button
                              onClick={() => onMovieClick(movie)}
                              className="text-primary-600 hover:text-primary-800 hover:underline text-left"
                            >
                              {movie.title || 'Untitled'}
                            </button>
                          ) : (
                            <span className="text-primary-600">{movie.title || 'Untitled'}</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">{movie.genres.join(', ')}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{movie.lastSeen}</td>
                        <td className="px-4 py-2 text-sm">
                          {editingScore === movie.id ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                min="0"
                                max="10"
                                value={tempScore}
                                onChange={(e) => setTempScore(Number(e.target.value))}
                                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <button
                                onClick={() => handleSaveScore(movie.id)}
                                className="text-green-600 hover:text-green-800"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-red-600 hover:text-red-800"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <span className={`font-medium ${
                              movie.score >= 7 ? 'text-green-600' : 
                              movie.score >= 5 ? 'text-blue-600' : 'text-red-600'
                            }`}>
                              {movie.score}
                            </span>
                          )}
                        </td>
                        {isAuthenticated && (
                          <td className="px-4 py-2 text-sm">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditScore(movie)}
                                className="text-primary-600 hover:text-primary-800"
                                title="Edit score"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteMovie(movie.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Delete movie"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Last 10 Movies Over 7.0 Table */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div 
              className="flex items-center justify-between mb-4 cursor-pointer"
              onClick={() => toggleSection('recentHighRated')}
            >
              <h3 className="text-lg font-semibold text-gray-900">Last 10 movies over 7.0 watched</h3>
              {expandedSections.recentHighRated ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
            {expandedSections.recentHighRated && (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg overflow-hidden">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Year</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Name</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Genres</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Seen</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Score</th>
                      {isAuthenticated && (
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {last10HighRated.length === 0 ? (
                      <tr>
                        <td colSpan={isAuthenticated ? 6 : 5} className="px-4 py-4 text-center text-sm text-gray-500">
                          No movies with score ≥ 7.0 and "Last Seen" dates found
                        </td>
                      </tr>
                    ) : (
                      last10HighRated.map((movie, index) => (
                      <tr key={movie.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2 text-sm text-gray-900">{movie.year}</td>
                        <td className="px-4 py-2 text-sm font-medium">
                          {onMovieClick ? (
                            <button
                              onClick={() => onMovieClick(movie)}
                              className="text-primary-600 hover:text-primary-800 hover:underline text-left"
                            >
                              {movie.title || 'Untitled'}
                            </button>
                          ) : (
                            <span className="text-primary-600">{movie.title || 'Untitled'}</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">{movie.genres.join(', ')}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{movie.lastSeen}</td>
                        <td className="px-4 py-2 text-sm">
                          {editingScore === movie.id ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                min="0"
                                max="10"
                                value={tempScore}
                                onChange={(e) => setTempScore(Number(e.target.value))}
                                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <button
                                onClick={() => handleSaveScore(movie.id)}
                                className="text-green-600 hover:text-green-800"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-red-600 hover:text-red-800"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <span className={`font-medium ${
                              movie.score >= 8 ? 'text-blue-600' : 'text-green-600'
                            }`}>
                              {movie.score}
                            </span>
                          )}
                        </td>
                        {isAuthenticated && (
                          <td className="px-4 py-2 text-sm">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditScore(movie)}
                                className="text-primary-600 hover:text-primary-800"
                                title="Edit score"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteMovie(movie.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Delete movie"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
